import React, { useCallback, useEffect, useRef, useState } from "react";
import DataGrid, { Column } from "devextreme-react/data-grid";
import Popup from "devextreme-react/popup";
import Form, {
  SimpleItem,
  Label,
  RequiredRule,
  Item,
} from "devextreme-react/form";
import Button from "devextreme-react/button";
import type { default as FormInstance } from "devextreme/ui/form";
import notify from "devextreme/ui/notify";
import axios, { AxiosError } from "axios";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import * as S from "../ComponentesGarantia/ComponentesGarantia.styles";
import * as GridS from "./Grid.styles";
import { ApiError } from "../../../services/apiError";
import {
  AsociarProductoComponenteDto,
  ComponenteGarantia,
  ProductoComponenteGarantia,
} from "../../../dtos/ComponenteGarantia";
import { componentesGarantiaService } from "../../../services/componentesGarantiaService";
import ComponenteGarantiaFormPopup from "../ComponentesGarantia/ComponenteGarantiaFormPopup";

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    return {
      message:
        error.response?.data?.message || "Ocurrió un error en el servidor.",
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return {
    message: "Ocurrió un error inesperado. Revisa tu conexión de red.",
  };
};

type ProductoComponentesSectionProps = {
  codigoProducto: number;
};

const formatComponenteLabel = (c: ComponenteGarantia) =>
  c.nombre ? `${c.codigo} — ${c.nombre}` : c.codigo;

const ProductoComponentesSection: React.FC<ProductoComponentesSectionProps> = ({
  codigoProducto,
}) => {
  const [componentes, setComponentes] = useState<ProductoComponenteGarantia[]>([]);
  const [catalogo, setCatalogo] = useState<ComponenteGarantia[]>([]);
  const [loading, setLoading] = useState(false);
  const [asociarVisible, setAsociarVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [nuevoComponenteVisible, setNuevoComponenteVisible] = useState(false);
  const [asociarForm, setAsociarForm] = useState<AsociarProductoComponenteDto>({
    componenteGarantiaId: null,
    especificacion: "",
    orden: 0,
    activo: true,
  });
  const [editItem, setEditItem] = useState<ProductoComponenteGarantia | null>(null);
  const [itemToDelete, setItemToDelete] = useState<ProductoComponenteGarantia | null>(
    null,
  );
  const asociarFormRef = useRef<FormInstance | null>(null);
  const editFormRef = useRef<FormInstance | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [links, cat] = await Promise.all([
        componentesGarantiaService.getByProducto(codigoProducto),
        componentesGarantiaService.getAll(true),
      ]);
      setComponentes(links ?? []);
      setCatalogo(cat ?? []);
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setLoading(false);
    }
  }, [codigoProducto]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const componentesDisponibles = catalogo.filter(
    (c) => !componentes.some((x) => x.componenteGarantiaId === c.id),
  );

  const openAsociar = () => {
    setAsociarForm({
      componenteGarantiaId: componentesDisponibles[0]?.id ?? null,
      especificacion: "",
      orden: componentes.length,
      activo: true,
    });
    setAsociarVisible(true);
  };

  const handleAsociar = async () => {
    const form = asociarFormRef.current;
    if (!form) return;
    const validation = await form.validate();
    if (!validation.isValid) {
      notify("Complete los campos requeridos.", "warning", 2000);
      return;
    }
    const formData = form.option("formData") as AsociarProductoComponenteDto;
    if (!formData.componenteGarantiaId) {
      notify("Seleccione un componente.", "warning", 2000);
      return;
    }
    try {
      await componentesGarantiaService.asociar(codigoProducto, {
        componenteGarantiaId: formData.componenteGarantiaId,
        especificacion: formData.especificacion.trim(),
        orden: formData.orden ?? 0,
        activo: formData.activo ?? true,
      });
      notify("Componente asociado al producto.", "success", 2000);
      setAsociarVisible(false);
      await loadData();
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    }
  };

  const handleEdit = (item: ProductoComponenteGarantia) => {
    setEditItem({ ...item });
    setEditVisible(true);
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    const form = editFormRef.current;
    if (!form) return;
    const validation = await form.validate();
    if (!validation.isValid) {
      notify("Complete los campos requeridos.", "warning", 2000);
      return;
    }
    const formData = form.option("formData") as ProductoComponenteGarantia;
    try {
      await componentesGarantiaService.actualizarAsociacion(
        codigoProducto,
        editItem.id,
        {
          especificacion: formData.especificacion?.trim(),
          orden: formData.orden,
          activo: formData.activo,
        },
      );
      notify("Asociación actualizada.", "success", 2000);
      setEditVisible(false);
      await loadData();
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    }
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      await componentesGarantiaService.desasociar(codigoProducto, itemToDelete.id);
      notify("Componente desasociado del producto.", "success", 2000);
      setDeleteVisible(false);
      setItemToDelete(null);
      await loadData();
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    }
  };

  const handleNuevoComponenteSaved = async (item: ComponenteGarantia) => {
    setCatalogo((prev) => [...prev, item]);
    setAsociarForm((prev) => ({ ...prev, componenteGarantiaId: item.id }));
    if (!asociarVisible) {
      setAsociarForm({
        componenteGarantiaId: item.id,
        especificacion: "",
        orden: componentes.length,
        activo: true,
      });
      setAsociarVisible(true);
    }
  };

  const EstatusCell = (cellInfo: { value: boolean }) => {
    const isActive = cellInfo.value;
    return (
      <div
        style={{
          padding: "4px 10px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 500,
          color: "#fff",
          backgroundColor: isActive ? "#16a34a" : "#71717a",
          textAlign: "center",
          display: "inline-block",
        }}
      >
        {isActive ? "Activo" : "Inactivo"}
      </div>
    );
  };

  const ActionsCell = (cellData: { data: ProductoComponenteGarantia }) => {
    const item = cellData.data;
    return (
      <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
        <Button hint="Editar" onClick={() => handleEdit(item)} stylingMode="text">
          <FiEdit size={18} color="#1d4ed8" />
        </Button>
        <Button
          hint="Desasociar"
          onClick={() => {
            setItemToDelete(item);
            setDeleteVisible(true);
          }}
          stylingMode="text"
        >
          <FiTrash2 size={18} color="#dc2626" />
        </Button>
      </div>
    );
  };

  return (
    <>
      <GridS.DevExtremeModalGlobalStyles />
      <S.SectionToolbar>
        <Button
          icon="add"
          text="Asociar componente"
          type="default"
          stylingMode="contained"
          onClick={openAsociar}
          disabled={componentesDisponibles.length === 0 && catalogo.length > 0}
        />
        <Button
          icon="plus"
          text="Nuevo componente"
          stylingMode="outlined"
          onClick={() => setNuevoComponenteVisible(true)}
        />
      </S.SectionToolbar>

      {componentesDisponibles.length === 0 && catalogo.length > 0 && (
        <p style={{ fontSize: 13, color: "#6B778C", margin: "0 0 12px" }}>
          Todos los componentes del catálogo ya están asociados a este producto.
        </p>
      )}

      <DataGrid
        dataSource={componentes}
        keyExpr="id"
        showBorders
        columnAutoWidth
        loadPanel={{ enabled: loading }}
        noDataText="Sin componentes asociados"
      >
        <Column dataField="codigoComponente" caption="Código" width={100} />
        <Column dataField="nombreComponente" caption="Nombre" width={200} />
        <Column dataField="especificacion" caption="Especificación" width={180} />
        <Column dataField="orden" caption="Orden" width={80} alignment="center" />
        <Column
          dataField="activo"
          caption="Estatus"
          alignment="center"
          cellRender={EstatusCell}
          width={110}
        />
        <Column
          caption="Acciones"
          alignment="center"
          width={120}
          cellRender={ActionsCell}
        />
      </DataGrid>

      <Popup
        visible={asociarVisible}
        onHiding={() => setAsociarVisible(false)}
        showTitle
        title="Asociar componente al producto"
        width={520}
        height="auto"
        className="custom-form-popup"
      >
        <S.FormContainer>
          <Form
            formData={asociarForm}
            onInitialized={(e) => {
              asociarFormRef.current = e.component ?? null;
            }}
          >
            <Item
              dataField="componenteGarantiaId"
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: componentesDisponibles,
                valueExpr: "id",
                displayExpr: formatComponenteLabel,
                searchEnabled: true,
                placeholder: "Seleccione un componente",
              }}
            >
              <Label text="Componente" />
              <RequiredRule message="Seleccione un componente." />
            </Item>
            <SimpleItem dataField="especificacion">
              <Label text="Especificación" />
              <RequiredRule message="La especificación es requerida." />
            </SimpleItem>
            <SimpleItem
              dataField="orden"
              editorType="dxNumberBox"
              editorOptions={{ min: 0, showSpinButtons: true }}
            >
              <Label text="Orden" />
            </SimpleItem>
            <Item
              dataField="activo"
              editorType="dxCheckBox"
              editorOptions={{ text: "Activo" }}
            />
          </Form>
        </S.FormContainer>
        <S.PopupFooter>
          <S.CancelButton onClick={() => setAsociarVisible(false)}>
            Cancelar
          </S.CancelButton>
          <S.SaveButton onClick={handleAsociar}>Guardar</S.SaveButton>
        </S.PopupFooter>
      </Popup>

      <Popup
        visible={editVisible}
        onHiding={() => setEditVisible(false)}
        showTitle
        title={`Editar: ${editItem?.codigoComponente ?? ""}`}
        width={520}
        height="auto"
        className="custom-form-popup"
      >
        <S.FormContainer>
          <Form
            formData={editItem}
            onInitialized={(e) => {
              editFormRef.current = e.component ?? null;
            }}
          >
            <SimpleItem dataField="especificacion">
              <Label text="Especificación" />
              <RequiredRule message="La especificación es requerida." />
            </SimpleItem>
            <SimpleItem
              dataField="orden"
              editorType="dxNumberBox"
              editorOptions={{ min: 0, showSpinButtons: true }}
            >
              <Label text="Orden" />
            </SimpleItem>
            <Item
              dataField="activo"
              editorType="dxCheckBox"
              editorOptions={{ text: "Activo" }}
            />
          </Form>
        </S.FormContainer>
        <S.PopupFooter>
          <S.CancelButton onClick={() => setEditVisible(false)}>Cancelar</S.CancelButton>
          <S.SaveButton onClick={handleSaveEdit}>Guardar</S.SaveButton>
        </S.PopupFooter>
      </Popup>

      <Popup
        visible={deleteVisible}
        onHiding={() => setDeleteVisible(false)}
        showTitle
        title="Confirmar desasociación"
        width={420}
        height="auto"
        className="custom-form-popup"
      >
        <div style={{ padding: 20, textAlign: "center" }}>
          <p>
            ¿Desasociar el componente{" "}
            <strong>{itemToDelete?.codigoComponente}</strong> de este producto?
          </p>
        </div>
        <S.PopupFooter>
          <S.CancelButton onClick={() => setDeleteVisible(false)}>
            Cancelar
          </S.CancelButton>
          <S.SaveButton onClick={confirmDelete}>Desasociar</S.SaveButton>
        </S.PopupFooter>
      </Popup>

      <ComponenteGarantiaFormPopup
        visible={nuevoComponenteVisible}
        editingItem={{ activo: true }}
        onHiding={() => setNuevoComponenteVisible(false)}
        onSaved={handleNuevoComponenteSaved}
      />
    </>
  );
};

export default ProductoComponentesSection;
